/**
 * Rundowns and playlists as resources (PLAN §6.5, §10.1): create, list, get,
 * soft-delete. Each is the head row of its own partition (`@acp/domain/row.js`
 * explains why), so what these routes return are rows — the same shape the
 * row API and the WS echo carry.
 *
 * A create is a convenience over a creation patch: the server mints the HLC
 * and writes the head row. The client MAY supply the id (a ULID it generated),
 * which makes a retried create idempotent — a create whose id already exists
 * with the same kind returns the existing row.
 */
import { playlistTimingSchema } from '@acp/domain/fields.js';
import { ordSchema, ulidSchema } from '@acp/domain/primitives.js';
import { rowSchema } from '@acp/domain/row.js';
import { z } from 'zod';
import { cursorSchema, PAGE_LIMIT_DESCRIPTION, PAGE_TOKEN_DESCRIPTION, pageLimitSchema, pageTokenSchema } from './rows.js';

const nameSchema = z.string().min(1).max(500);

export const createRundownRequestSchema = z
  .object({
    /** Client-generated id for an idempotent retry; the server mints one when absent. */
    rundownId: ulidSchema.optional(),
    name: nameSchema,
    /** The playlist to place it in, or null for a stand-alone rundown. */
    playlistId: ulidSchema.nullable(),
    /** Position among the playlist's rundowns; null = after the last. */
    ord: ordSchema.nullable(),
  })
  .strict();
export type CreateRundownRequest = z.infer<typeof createRundownRequestSchema>;

export const rundownResponseSchema = z.object({ rundown: rowSchema }).strict();

/** `GET /rundowns/{rundownId}`: the head row, and the cursor to start a `changedSince` catch-up from. */
export const getRundownResponseSchema = z.object({ rundown: rowSchema, cursor: cursorSchema }).strict();

export const listRundownsQuerySchema = z
  .object({
    playlistId: ulidSchema.optional().describe('Only the rundowns of this playlist.'),
    pageToken: pageTokenSchema.optional().describe(PAGE_TOKEN_DESCRIPTION),
    limit: pageLimitSchema.optional().describe(PAGE_LIMIT_DESCRIPTION),
    includeDeleted: z.enum(['true', 'false']).optional().describe('Include soft-deleted rundowns (to offer an undelete). Default false.'),
  })
  .strict();

export const listRundownsResponseSchema = z
  .object({ rundowns: z.array(rowSchema), nextPageToken: pageTokenSchema.nullable() })
  .strict();

export const createPlaylistRequestSchema = z
  .object({
    playlistId: ulidSchema.optional(),
    name: nameSchema,
    timing: playlistTimingSchema,
  })
  .strict();
export type CreatePlaylistRequest = z.infer<typeof createPlaylistRequestSchema>;

export const playlistResponseSchema = z.object({ playlist: rowSchema }).strict();

export const getPlaylistResponseSchema = z.object({ playlist: rowSchema, cursor: cursorSchema }).strict();

export const listPlaylistsQuerySchema = z
  .object({
    pageToken: pageTokenSchema.optional().describe(PAGE_TOKEN_DESCRIPTION),
    limit: pageLimitSchema.optional().describe(PAGE_LIMIT_DESCRIPTION),
    includeDeleted: z.enum(['true', 'false']).optional().describe('Include soft-deleted playlists. Default false.'),
  })
  .strict();

export const listPlaylistsResponseSchema = z
  .object({ playlists: z.array(rowSchema), nextPageToken: pageTokenSchema.nullable() })
  .strict();

export const playlistPathSchema = z.object({ playlistId: ulidSchema.describe('The playlist’s id (a ULID).') }).strict();

